import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { ReactNode } from "react";

import APPrintablePage from "../../../../components/APPrintablePage";
import APCalculation from "../../../../components/calculations/APCalculation";
import APCalculationInput from "../../../../components/calculations/APCalculationInput";
import APCalculationOutput from "../../../../components/calculations/APCalculationOutput";
import APPeriodicCalculations from "../../../../components/calculations/APPeriodicCalculation";
import { toDollars } from "../../../../helpers/currencies";
import { FinanceComparisonCalculations as FinanceComparisonCalculationsType } from "../../../../types/Calculation";

type DisplayedFields = {
  onroadVehiclePrice?: boolean;
  carLoanTermYrs?: boolean;
  termYrs?: boolean;
  balloonPayment?: boolean;

  carLoanInterestRate?: boolean;
  termMths?: boolean;
  loanPayment?: boolean;
  totalPayments?: boolean;

  residualIncGst?: boolean;
  runningCosts?: boolean;
  lifetimeCost?: boolean;
  interest?: boolean;
};

export const FINANCE_COMPARISON_CAR_LOAN_ALL_FIELDS: DisplayedFields = {
  onroadVehiclePrice: true,
  carLoanTermYrs: true,
  termYrs: true,
  balloonPayment: true,

  carLoanInterestRate: true,
  termMths: true,
  loanPayment: true,
  totalPayments: true,

  residualIncGst: true,
  runningCosts: true,
  lifetimeCost: true,
  interest: true,
};

const FinanceComparisonCarLoanCalculations = ({
  calculations,
  displayedFields,
  pageLinks,
}: {
  calculations: FinanceComparisonCalculationsType;
  displayedFields: DisplayedFields;
  pageLinks?: ReactNode;
}) => {
  const { inputs, carloan } = calculations;

  return (
    <APPrintablePage
      topContent={
        <Stack spacing={2}>
          {pageLinks}
          <Box>
            {displayedFields.onroadVehiclePrice && (
              <APCalculationInput
                name="Onroad Vehicle Price"
                value={inputs.onroadVehiclePrice}
                currency
              />
            )}
            {displayedFields.carLoanTermYrs && (
              <APCalculationInput
                name="Car Loan Term Years"
                value={inputs.carLoanTermYrs}
              />
            )}
            {displayedFields.termYrs && (
              <APCalculationInput name="Term Years" value={inputs.termYrs} />
            )}

            {displayedFields.balloonPayment && (
              <APCalculationOutput
                calculation={carloan.balloonPayment}
                currency
              />
            )}
          </Box>
          <Box>
            {displayedFields.carLoanInterestRate && (
              <APCalculationInput
                name="Car Loan Interest Rate"
                value={inputs.carLoanInterestRate}
                percentage
              />
            )}
            {displayedFields.termMths && (
              <APCalculationInput name="Term Months" value={inputs.termMths} />
            )}
            {displayedFields.loanPayment && (
              <APCalculationOutput
                calculation={carloan.loanRepayment}
                currency
              />
            )}
            {displayedFields.totalPayments && (
              <APCalculationOutput
                calculation={carloan.totalPayments}
                currency
              />
            )}
          </Box>
          <Box>
            {displayedFields.residualIncGst && (
              <APCalculationInput
                name="Residual Inc GST"
                value={inputs.residual.incGst}
                currency
              />
            )}
            {displayedFields.runningCosts && (
              <APCalculationInput
                name="Running Costs"
                value={inputs.runningCosts.total}
                currency
              />
            )}
            {displayedFields.lifetimeCost && (
              <APCalculation
                name={carloan.lifetimeCost.name}
                value={toDollars(carloan.lifetimeCost.result.total, 2)}
                tooltip={carloan.lifetimeCost.formula}
              />
            )}
            {displayedFields.interest && (
              <APCalculationOutput calculation={carloan.interest} currency />
            )}
          </Box>
        </Stack>
      }
      bottomContent={
        <Box sx={{ maxWidth: 750 }}>
          {displayedFields.lifetimeCost && (
            <APPeriodicCalculations
              calculations={[
                {
                  name: "Lifetime Cost",
                  amounts: carloan.lifetimeCost.result,
                },
              ]}
            />
          )}
        </Box>
      }
    />
  );
};

export default FinanceComparisonCarLoanCalculations;
